import React, { forwardRef } from 'react';
import { Input } from '@mui/joy';
import { IMaskInput } from 'react-imask';

const DEFAULT_DEFINITIONS = {
  '#': /[0-9]/, '0': /[0-9]/, '9': /[0-9]/,
  'A': /[A-Z]/, 'a': /[a-z]/, '!': /[A-Za-z]/, '*': /./,
};
const RUSSIAN_PHONE_MASK = '+7 (000) 000-00-00';

function phoneMaskValue(value) {
  if (value == null || value === '') return '';
  const text = String(value);
  const digits = text.replace(/\D/g, '');
  // IMask treats an unformatted domestic 8 as the first area-code digit
  // and truncates the last digit. Normalize complete saved/pasted phones
  // before handing them to the fixed +7 mask.
  if (digits.length === 11 && /^[78]/.test(digits)) return `+7${digits.slice(1)}`;
  if (digits.length === 10 && !/^\s*\+7/.test(text)) return `+7${digits}`;
  return text;
}

function preparePhoneInput(value, masked, flags) {
  // prepare also runs for IMask's internal unmasked-value refresh. Only
  // normalize a complete phone pasted into an empty input, once.
  const digits = value.replace(/\D/g, '');
  if (flags?.input && !masked.unmaskedValue && digits.length === 11 && /^[78]/.test(digits)) {
    const normalized = phoneMaskValue(value);
    return flags.raw ? normalized.replace(/\D/g, '').slice(-10) : normalized;
  }
  return value;
}

/**
 * MaskedInput - компонент ввода с маской, стилизованный под MUI Joy
 * 
 * @param {Object} props
 * @param {string} props.mask - Маска для ввода (например: "+7 (000) 000-00-00")
 * @param {Object} props.definitions - Кастомные определения символов маски
 * @param {boolean} props.lazy - Показывать маску только при фокусе
 * @param {boolean} props.overwrite - Режим перезаписи при вводе
 * @param {string} props.placeholderChar - Символ-заполнитель
 * @param {any} props.value - Контролируемое значение
 * @param {function} props.onChange - Обработчик изменения значения
 * @param {function} props.onAccept - Обработчик принятия значения
 * @param {Object} props.inputProps - Дополнительные пропсы для IMask
 * @param {ref} ref - React ref
 * @param {Object} ...rest - Все остальные пропсы передаются в Joy Input
 */
const MaskedInput = forwardRef((props, ref) => {
  const {
    mask,
    definitions,
    lazy = false,
    overwrite = 'shift',
    placeholderChar = '_',
    value,
    onChange,
    onAccept,
    inputProps = {},
    ...inputJoyProps
  } = props;

  // Объединяем обработчики
  const handleAccept = (val, maskRef, inputEvent) => {
    // IMask also accepts values on mount and controlled-prop updates. Those
    // events must never mark a saved form as edited or overwrite its phone.
    if (!inputEvent) return;
    if (onAccept) {
      onAccept(val, maskRef, inputEvent);
    }
    // Создаем синтетическое событие для onChange
    if (onChange) {
      const event = {
        target: {
          name: props.name,
          value: val,
        },
      };
      onChange(event);
    }
  };

  return (
    <Input
      {...inputJoyProps}
      value={mask === RUSSIAN_PHONE_MASK ? phoneMaskValue(value) : value}
      slotProps={{
        input: {
          color: "#572AC3",
          component: IMaskInput,
          mask: mask,
          inputRef: ref,
          definitions: definitions || DEFAULT_DEFINITIONS,
          ...(mask === RUSSIAN_PHONE_MASK ? { prepare: preparePhoneInput } : {}),
          lazy: lazy,
          overwrite: overwrite,
          placeholderChar: placeholderChar,
          onAccept: handleAccept,
          ...inputProps,
        },
      }}
    />
  );
});

MaskedInput.displayName = 'MaskedInput';

export default MaskedInput;
